import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Board } from '../../types';
import { useApp } from '../../context/AppContext';
import { getDaysInMonth, getFirstDayOfMonth, getPriorityConfig } from '../../utils/helpers';

interface CalendarViewProps {
  board: Board;
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export default function CalendarView({ board }: CalendarViewProps) {
  const { getTasksForBoard, dispatch } = useApp();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const tasks = getTasksForBoard(board.id).filter((t) => t.dueDate);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const getTasksForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter((t) => t.dueDate && t.dueDate.startsWith(dateStr));
  };

  const isToday = (day: number) => {
    return (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day
    );
  };

  const blanks = Array.from({ length: firstDay }, (_, i) => i);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <div className="p-4 md:p-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800">
            {MONTH_NAMES[month]} {year}
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={prevMonth}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Mês anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => {
                setYear(today.getFullYear());
                setMonth(today.getMonth());
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 hover:bg-brand-50 transition-colors"
            >
              Hoje
            </button>
            <button
              onClick={nextMonth}
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Próximo mês"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7">
          {DAY_NAMES.map((day) => (
            <div
              key={day}
              className="py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100"
            >
              {day}
            </div>
          ))}

          {blanks.map((i) => (
            <div key={`blank-${i}`} className="min-h-[80px] md:min-h-[110px] border-b border-r border-slate-100 bg-slate-50/50" />
          ))}

          {days.map((day) => {
            const dayTasks = getTasksForDay(day);
            const todayClass = isToday(day);

            return (
              <div
                key={day}
                className={`min-h-[80px] md:min-h-[110px] border-b border-r border-slate-100 p-1.5 ${
                  todayClass ? 'bg-brand-50/40' : 'hover:bg-slate-50'
                } transition-colors`}
              >
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                    todayClass
                      ? 'bg-brand-500 text-white'
                      : 'text-slate-600'
                  }`}
                >
                  {day}
                </span>
                <div className="mt-1 space-y-0.5">
                  {dayTasks.slice(0, 3).map((task) => {
                    const p = getPriorityConfig(task.priority);
                    return (
                      <button
                        key={task.id}
                        onClick={() => dispatch({ type: 'SELECT_TASK', payload: task.id })}
                        className="w-full text-left px-1.5 py-0.5 rounded text-[10px] md:text-xs font-medium truncate hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: `${p.dot.replace('bg-', '')}20`, color: p.dot.replace('bg-', '') }}
                        title={task.title}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${p.dot}`} />
                        <span className="text-slate-700">{task.title}</span>
                      </button>
                    );
                  })}
                  {dayTasks.length > 3 && (
                    <span className="text-[10px] text-slate-400 pl-1">+{dayTasks.length - 3} mais</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
